/**
 * Walkthrough — first-sign-in product tour for the operator console.
 *
 * A centered, stepped modal shown once per browser (localStorage
 * `parkify_walkthrough_seen_v1`). It leads with how to use Avery, then a grid
 * covering EVERY section in the caller's sidebar — fed the same
 * permission/feature-filtered list App passes to <Sidebar>, so the tour only
 * ever shows sections the user can actually open. Re-openable anywhere via
 * openWalkthrough() (the "Take a tour" link on Home).
 *
 * Mounted in App (after sign-in) alongside AveryWidget. Finishing or skipping
 * also collapses the Home welcome banner so first-run users aren't onboarded
 * twice.
 */
import { useEffect, useMemo, useState } from 'react'
import type { NavItem } from './Sidebar'
import { askAvery } from './AveryWidget'

import {
  WALKTHROUGH_SEEN_KEY, WELCOME_DISMISSED_KEY, readFirstRunFlag, writeFirstRunFlag,
} from '../utils/firstRunFlags'
import { useAuth } from '../auth/AuthContext'
export { WALKTHROUGH_SEEN_KEY }
const OPEN_EVENT = 'parkify:walkthrough:open'

/** Fired on finish/skip so Home collapses its welcome banner live (it only
 *  reads the localStorage flag on mount). */
export const WELCOME_DISMISS_EVENT = 'parkify:welcome:dismiss'

/** Open the walkthrough from anywhere (e.g. Home's "Take a tour" link). */
export function openWalkthrough() {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT))
}

// One-line "what it's for" per sidebar section, keyed by NavItem id. A section
// without an entry still appears in the grid (label only) so the tour never
// silently drops an item the user can see.
const SECTION_BLURBS: Record<string, string> = {
  home:          'Your live dashboard — today’s revenue, active sessions, and recent activity at a glance.',
  avery:         'The full-screen home for your AI assistant — ask a question, or pick up a recent thread.',
  zones:         'Your properties and the payable areas inside them — pricing, hours of operation, rate windows, and dated closures.',
  qr:            'Generate and manage the QR codes drivers scan to start a session.',
  transactions:  'Browse every parking session and issue refunds in a couple of clicks.',
  refund_requests: 'Review customer refund requests and record a decision for each request.',
  accounting:    'Payouts, platform fees, and financial reporting for your locations.',
  disputes:      'Track card chargebacks and respond with the evidence we assemble.',
  analytics:     '30-day trends — revenue, occupancy, and your busiest hours.',
  tenants:       'The businesses that lease here — their contacts, their locations, and what they’re billed.',
  permits:       'Issue permits to a tenant’s staff and manage their parker allow-lists.',
  validations:   'Comp or discount a visitor’s parking, with spend limits and rotating codes per program.',
  subscriptions: 'Recurring parking a driver buys at checkout and is charged for automatically.',
  enforcement:   'Review license-plate scans and enforcement activity across your locations.',
  client_admin:  'Manage your organization — team members, branding, Stripe payouts, and feature settings.',
  support:       'Triage customer emails and reply, with Avery drafting answers for you.',
  customers:     'Look up any driver by plate, email, or phone — plus their history.',
  kpi_revenue:   'Platform-wide revenue and growth across every client.',
  integrations:  'Connect Parkify to your partners and other tools.',
  parkify_admin: 'Internal Parkify controls — tenants, clients, and platform settings.',
  kill_switches: 'Master switches for Avery’s autonomy — restricted to named admins.',
}

const AVERY_EXAMPLES = [
  'How is today going?',
  'Which zone earned the most this month?',
  "Show me today's refunds",
]

interface Step { key: string; eyebrow: string; title: string; body: React.ReactNode }

export default function Walkthrough({
  items, hasAvery, firstName,
}: {
  items: NavItem[]
  hasAvery: boolean
  firstName: string
}) {
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState(0)
  // Under View as, "seen" is the TARGET's first run, not the operator's (see
  // utils/firstRunFlags). App keys this component on the lens, so it re-runs
  // the auto-open when a lens opens or closes.
  const { viewingAs } = useAuth()
  const lensEmail = viewingAs?.email ?? null

  // Auto-open on first sign-in (per browser).
  useEffect(() => {
    if (!readFirstRunFlag(WALKTHROUGH_SEEN_KEY, lensEmail, true)) { setStep(0); setOpen(true) }
  }, [lensEmail])

  // Replay trigger from openWalkthrough().
  useEffect(() => {
    const onOpen = () => { setStep(0); setOpen(true) }
    window.addEventListener(OPEN_EVENT, onOpen)
    return () => window.removeEventListener(OPEN_EVENT, onOpen)
  }, [])

  const finish = () => {
    setOpen(false)
    writeFirstRunFlag(WALKTHROUGH_SEEN_KEY, lensEmail)
    // Don't double-onboard: collapse the Home welcome banner too.
    writeFirstRunFlag(WELCOME_DISMISSED_KEY, lensEmail)
    // Nudge Home to hide the welcome banner this session (it reads the flag
    // only on mount).
    window.dispatchEvent(new CustomEvent(WELCOME_DISMISS_EVENT))
  }

  // Close the tour, then act — so the opened Avery panel / navigated screen
  // isn't hidden behind the (briefly still-mounted) overlay.
  const tryPrompt = (text: string) => { finish(); askAvery(text) }
  const goTo = (id: string) => {
    finish()
    window.dispatchEvent(new CustomEvent('parkify:navigate', { detail: { screen: id } }))
  }

  const steps = useMemo<Step[]>(() => {
    const list: Step[] = []
    list.push({
      key: 'welcome',
      eyebrow: 'Welcome',
      title: `Welcome to the Parkify console, ${firstName}`,
      body: (
        <p style={pStyle}>
          This is your live operations hub — the dashboard refreshes every 60 seconds with today’s
          revenue, active sessions, and recent activity. This quick tour shows you Avery, your AI
          assistant, and everything you can do from here. It takes about 30 seconds.
        </p>
      ),
    })
    if (hasAvery) {
      list.push({
        key: 'avery',
        eyebrow: 'Your AI assistant',
        title: 'Meet Avery',
        body: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={pStyle}>
              Avery answers questions about your locations and can tee up actions for you to confirm —
              refunds, rate changes, and more. Three ways to reach her:
            </p>
            <ul style={ulStyle}>
              <li><strong>The search bar</strong> at the top of this page — type a question, or a
                plate / location / transaction, and press enter.</li>
              <li><strong>⌘K</strong> (Ctrl+K on Windows) — opens the full chat from any screen.</li>
              <li><strong>“Ask Avery” in the sidebar</strong> — the full-screen version, with your
                recent threads.</li>
            </ul>
            <div style={tryLabel}>Try one</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {AVERY_EXAMPLES.map(ex => (
                <button
                  key={ex}
                  onClick={() => tryPrompt(ex)}
                  style={chipStyle}
                  onMouseEnter={e => { e.currentTarget.style.background = 'color-mix(in srgb, var(--parkify-accent) 10%, transparent)' }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--color-white)' }}
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        ),
      })
    }
    list.push({
      key: 'workspace',
      eyebrow: 'Your workspace',
      title: 'Everything in your console',
      body: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={pStyle}>
            Here’s every section you have access to — click any one to jump straight there, or find
            them all in the left sidebar.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
            {items.map(it => (
              <button
                key={it.id}
                onClick={() => goTo(it.id)}
                style={sectionCard}
                // Full `border`, not a borderColor override: sectionCard sets the
                // shorthand, and mixing the two makes React warn and can leave the
                // colour stuck on a rerender.
                onMouseEnter={e => { e.currentTarget.style.border = '1px solid var(--parkify-accent)'; e.currentTarget.style.transform = 'translateY(-1px)' }}
                onMouseLeave={e => { e.currentTarget.style.border = '1px solid var(--color-gray-200)'; e.currentTarget.style.transform = 'none' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--parkify-accent)' }}>
                  <span style={{ display: 'inline-flex' }}>{it.icon}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-black)' }}>{it.label}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-gray-500)', marginTop: 5, lineHeight: 1.45 }}>
                  {SECTION_BLURBS[it.id] ?? 'Open this section.'}
                </div>
              </button>
            ))}
          </div>
        </div>
      ),
    })
    list.push({
      key: 'finish',
      eyebrow: 'You’re all set',
      title: 'Ready when you are',
      body: (
        <p style={pStyle}>
          That’s the tour. You can reopen it any time from “Take a tour” on the home screen
          {hasAvery ? ', or just ask Avery if you get stuck' : ''}. Welcome aboard!
        </p>
      ),
    })
    return list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, hasAvery, firstName])

  // Esc closes (counts as skip); lock body scroll while open.
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') finish() }
    window.addEventListener('keydown', onKey)
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey) }
    // finish only closes over lensEmail, fixed for this mount (App keys it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null
  const idx = Math.min(step, steps.length - 1)
  const s = steps[idx]!
  const isLast = idx >= steps.length - 1

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Console walkthrough"
      onMouseDown={e => { if (e.target === e.currentTarget) finish() }}
      style={overlay}
    >
      <div onMouseDown={e => e.stopPropagation()} style={card}>
        <div style={cardHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span aria-hidden style={avatar}>A</span>
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '2px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.9)' }}>
              {s.eyebrow}
            </span>
          </div>
          <button onClick={finish} aria-label="Close tour" style={closeBtn}>×</button>
        </div>

        <div style={cardBody}>
          <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 12px', letterSpacing: '-0.3px', color: 'var(--color-black)' }}>
            {s.title}
          </h2>
          {s.body}
        </div>

        <div style={cardFooter}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {steps.map((st, i) => (
              <span key={st.key} aria-hidden style={{
                width: i === idx ? 22 : 7, height: 7, borderRadius: 999,
                background: i === idx ? 'var(--parkify-accent)' : 'var(--color-gray-300)',
                transition: 'width 160ms ease, background 160ms ease',
              }} />
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {!isLast && <button onClick={finish} style={skipBtn}>Skip</button>}
            {idx > 0 && <button onClick={() => setStep(n => Math.max(0, n - 1))} style={ghostBtn}>Back</button>}
            {isLast
              ? <button onClick={finish} style={primaryBtn}>Get started</button>
              : <button onClick={() => setStep(n => Math.min(steps.length - 1, n + 1))} style={primaryBtn}>Next</button>}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── styles ──────────────────────────────────────────────────────────────────
const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 1100,
  background: 'rgba(20,10,28,0.55)',
  backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  fontFamily: "'Montserrat', 'Avenir', sans-serif",
}
const card: React.CSSProperties = {
  width: 'min(560px, 100%)',
  maxHeight: 'min(680px, calc(100dvh - 64px))',
  background: 'var(--color-white)',
  borderRadius: 16, overflow: 'hidden',
  display: 'flex', flexDirection: 'column',
  boxShadow: '0 24px 60px rgba(0,0,0,0.4), 0 6px 18px color-mix(in srgb, var(--parkify-accent) 25%, transparent)',
}
const cardHeader: React.CSSProperties = {
  flexShrink: 0,
  background: 'linear-gradient(135deg, var(--parkify-accent-light) 0%, var(--parkify-accent) 50%, var(--parkify-accent-strong) 100%)',
  color: '#fff', padding: '14px 18px',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
}
const avatar: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 26, height: 26, borderRadius: '50%',
  background: 'var(--color-white)', color: 'var(--parkify-accent)',
  fontWeight: 800, fontSize: 13, fontFamily: 'monospace',
}
const closeBtn: React.CSSProperties = {
  background: 'rgba(255,255,255,0.14)', border: 'none', color: '#fff',
  width: 28, height: 28, borderRadius: 8, cursor: 'pointer',
  fontSize: 18, lineHeight: 1, fontFamily: 'inherit',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
}
const cardBody: React.CSSProperties = {
  flex: 1, overflowY: 'auto', padding: '22px',
}
const cardFooter: React.CSSProperties = {
  flexShrink: 0, padding: '14px 22px',
  borderTop: '1px solid var(--color-gray-200)',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
}
const pStyle: React.CSSProperties = { fontSize: 14, lineHeight: 1.6, color: 'var(--color-gray-700)', margin: 0 }
const ulStyle: React.CSSProperties = {
  margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 1.6, color: 'var(--color-gray-700)',
  display: 'flex', flexDirection: 'column', gap: 6,
}
const tryLabel: React.CSSProperties = {
  fontSize: 11, fontWeight: 800, letterSpacing: '1.5px', textTransform: 'uppercase',
  color: 'var(--color-gray-500)', marginTop: 2,
}
const chipStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, textAlign: 'left',
  padding: '7px 14px', borderRadius: 'var(--radius-chip)', whiteSpace: 'nowrap',
  background: 'var(--color-white)', color: 'var(--parkify-accent)',
  border: '1.5px solid var(--parkify-accent)', cursor: 'pointer', fontFamily: 'inherit',
  transition: 'background 120ms ease',
}
const sectionCard: React.CSSProperties = {
  textAlign: 'left', padding: '12px 14px', borderRadius: 10,
  background: 'var(--color-white)', border: '1px solid var(--color-gray-200)',
  cursor: 'pointer', fontFamily: 'inherit',
  transition: 'border-color 150ms, transform 150ms',
}
const ghostBtn: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, padding: '9px 16px', borderRadius: 'var(--radius-btn)',
  background: 'transparent', color: 'var(--color-gray-700)',
  border: '1px solid var(--color-gray-300)', cursor: 'pointer', fontFamily: 'inherit',
}
const skipBtn: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, padding: '9px 12px', borderRadius: 'var(--radius-btn)',
  background: 'transparent', color: 'var(--color-gray-500)',
  border: 'none', cursor: 'pointer', fontFamily: 'inherit',
}
const primaryBtn: React.CSSProperties = {
  fontSize: 13, fontWeight: 700, letterSpacing: '0.3px', padding: '9px 20px', borderRadius: 'var(--radius-btn)',
  background: 'linear-gradient(135deg, var(--parkify-accent-light) 0%, var(--parkify-accent) 50%, var(--parkify-accent-strong) 100%)',
  color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4), 0 2px 6px rgba(92,46,112,0.45)',
}
